import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Buttons from '../../Components/Buttons/Buttons';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Header from '../../Components/Header/Header';
import SignUpTitle from '../../Components/SignUpTitle/SignUpTitle';
import CustomSelect from '../../Components/CustomSelect/CustomSelect';
import { checkZipCode } from '../../Utils/checkZipCode';
import { useApi } from "../../Services/api";
import { useGlobalState } from '../../Contexts/GlobalStateContext';
import { countries, allStates } from '../../Components/CountryStateData/CountryStateData';
import './CompanySignUp.css';

const CompanySignUp = () => {
  const { setTempClientId, setUniqueToken, tempClientId } = useGlobalState();
  const [companyName, setCompanyName] = useState('');
  const [companyAddressLine1, setCompanyAddressLine1] = useState('');
  const [companyAddressLine2, setCompanyAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState({ label: 'United States', value: '1' });
  const [stateProvince, setStateProvince] = useState(null);
  const [zipCode, setZipCode] = useState('');
  const [validated, setValidated] = useState(false);
  const [stateInvalid, setStateInvalid] = useState(false);
  const [zipCodeError, setZipCodeError] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');
  const [companyAddressLine1Error, setCompanyAddressLine1Error] = useState('');
  const [cityError, setCityError] = useState('');
  const navigate = useNavigate();
  const api = useApi();
  const location = useLocation();

  useEffect(() => {
    if (tempClientId && location.state) {
      setCompanyName(location.state.ClientName);
      setCompanyAddressLine1(location.state.CompanyAddressLine1);
      setCompanyAddressLine2(location.state.CompanyAddressLine2);
      setCity(location.state.City);
      let countryValue = countries.find(item => parseInt(item.value, 10) === location.state.CountryId);
      setCountry(countryValue);
      let filteredStates = allStates.filter(state => state.country === countryValue.value);
      let stateValue = filteredStates.find(stateitem => stateitem.value === location.state.StateAndProvince);
      setStateProvince(stateValue);
      console.log(location.state.ZipCode);
      if (location.state.ZipCode.length > 5) {
        setZipCode(location.state.ZipCode.slice(0, 3) + ' ' + location.state.ZipCode.slice(3));
      } else {
        setZipCode(location.state.ZipCode);
      }
      
    }
  }, [tempClientId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setValidated(true);
    setCompanyNameError(companyName ? '' : 'required field');
    setCompanyAddressLine1Error(companyAddressLine1 ? '' : 'required field');
    setCityError(city ? '' : 'required field');
    setStateInvalid(!stateProvince || stateProvince.label === 'State/Province');

    if (!zipCode) {
      setZipCodeError('required field');
    } else if (!checkZipCode(zipCode, country)) {
      setZipCodeError('invalid!');
    } else { 
      setZipCodeError(''); 
    }

    const form = event.currentTarget;
    if (form.checkValidity() === false || !companyName || !companyAddressLine1 || !city || !stateProvince || !country || !checkZipCode(zipCode, country)) {
      event.stopPropagation();
      return;
    }
    let tempZipCode = zipCode;
    if (country.value === '38') {
      // remove the gap in the middle
      tempZipCode = tempZipCode.replace(' ', '');
    }
    const createStep = async () => {
      try {
        const body = {
          TempClientId: tempClientId,
          ClientName: companyName,
          CompanyAddressLine1: companyAddressLine1,
          CompanyAddressLine2: companyAddressLine2,
          City: city,
          StateAndProvince: stateProvince.value,
          Country: country.value,
          ZipCode: tempZipCode,
        };

        const response = await api.post(`Client/Create/Step/${1}`, body);
        if (response.status === 200 && !response.data.Error) {
          if (response.data.TempClientId) {
            setTempClientId(response.data.TempClientId);
          }
          setUniqueToken(response.data.UniqueToken);
          navigate('/UserSignUp');
        } else {
          throw new Error(response.data.Error || 'Unknown error');
        }
      } catch (error) {
        console.error(error);
      }
    }

    createStep();
  };

  const handleInputChange = (setter, errorSetter) => (e) => {
    const { value } = e.target;
    setter(value);
    if (validated) {
      if (!value) {
        errorSetter('required field');
      } else {
        errorSetter('');
      }
    }
  };


  const handleZipCodeChange = (e) => {
    const { value } = e.target;
    let formattedValue = value;
    let zipCodeRegex;
  
    if (country.value === '1') {
      formattedValue = value.replace(/\D/g, '').slice(0, 5);
      zipCodeRegex = /^\d{5}(-\d{4})?$/;
    } else if (country.value === '38') {
      formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (formattedValue.length > 3) {
        formattedValue = formattedValue.slice(0, 3) + ' ' + formattedValue.slice(3, 6);
      }
      zipCodeRegex = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;
    }
    setZipCode(formattedValue); 
    if (validated) {
      if (!formattedValue) {
        setZipCodeError('required field');
      } else if (!zipCodeRegex.test(formattedValue)) {
        setZipCodeError('invalid!');
      } else { 
        setZipCodeError(''); 
      }
    }
  };

  let filteredStates;
  try {
    filteredStates = allStates.filter(state => state.country === country.value);
  } catch (error) {
    filteredStates = []
  }

  
  return (
    <>
      <Header />
      <SignUpTitle stepNumber={1} />
      <div className="form-container">
        <Form className='form-content' noValidate validated={validated} onSubmit={handleSubmit}>
          <Form.Group controlId="validationCustom01">
            <div className='form-group-inline'>
              <Form.Label>Company Name*</Form.Label>
              <div className="invalid-feedback" style={{ display: companyNameError ? 'block' : 'none' }}>
                {companyNameError}
              </div>
            </div>
            <Form.Control
              required
              type="text"
              placeholder="Enter your company name"
              name="organization"
              autoComplete="organization"
              value={companyName}
              onChange={handleInputChange(setCompanyName, setCompanyNameError)}
              className="step-inputBox"
            />
          </Form.Group>

          <Form.Group controlId="validationCustom02">
            <div className='form-group-inline'>
              <Form.Label>Company Address Line 1*</Form.Label>
              <div className="invalid-feedback" style={{ display: companyAddressLine1Error ? 'block' : 'none' }}>
                {companyAddressLine1Error}
              </div> 
            </div>
            <Form.Control
              required
              type="text"
              placeholder="Enter your company address"
              name="address-line1"
              autoComplete="address-line1"
              value={companyAddressLine1}
              onChange={handleInputChange(setCompanyAddressLine1, setCompanyAddressLine1Error)}
              className="step-inputBox"
            />
          </Form.Group>

          <Form.Group controlId="validationCustom03">
            <div className='form-group-inline'>
              <Form.Label>Company Address Line 2</Form.Label>
            </div>
            <Form.Control
              type="text"
              placeholder="Enter your company address"
              name="address-line2"
              autoComplete="address-line2"
              value={companyAddressLine2}
              onChange={(e) => {
                setCompanyAddressLine2(e.target.value);
              }}
              className="step-inputBox"
            />
          </Form.Group>

          <Row className="step-row">
            <Form.Group as={Col} controlId="validationCustom04">
              <div className='form-group-inline'>
                <Form.Label>City*</Form.Label>
                <div className="invalid-feedback" style={{ display: cityError ? 'block' : 'none' }}>
                  {cityError}
                </div>
              </div>
              <Form.Control
                required
                type="text"
                placeholder="City"
                name="address-level2"
                autoComplete="address-level2"
                value={city}
                onChange={handleInputChange(setCity, setCityError)}
                className='step-inputBox'
              />
            </Form.Group>

            <Form.Group as={Col} controlId="validationCustom05">
              <div className='form-group-inline'>
                <Form.Label>State/Province*</Form.Label>
                <div className="invalid-feedback" style={{ display: stateInvalid ? 'block' : 'none' }}>
                  required field
                </div>
              </div>
              <CustomSelect
                key={country.value}
                options={filteredStates}
                placeholder="Select a state/province"
                onSelect={(value) => {
                  const selectedState = filteredStates.find(s => s.value === value);
                  setStateProvince(selectedState);
                  setStateInvalid(false);
                }}
                value={stateProvince?.value}
              />
            </Form.Group>
          </Row>

          <Row className="step-row">
            <Form.Group as={Col} controlId="validationCustom06">
              <div className="form-group-inline">
                <Form.Label>Country*</Form.Label>
              </div>
              <CustomSelect
                options={countries}
                placeholder="Select a country"
                onSelect={(value) => {
                  const selectedCountry = countries.find(c => c.value === value);
                  if (country.value !== selectedCountry.value) {
                    setCountry(selectedCountry);
                    setStateProvince(null);
                    setZipCode('');
                  }
                }}
                value={country?.value}
                defaultValue="1"
              />
            </Form.Group>
            <Form.Group as={Col} controlId="validationCustom07">
              <div className="form-group-inline">
                <Form.Label>Zip Code*</Form.Label>
                <div className="invalid-feedback" style={{ display: zipCodeError ? 'block' : 'none' }}>
                  {zipCodeError}
                </div>
              </div>
              <Form.Control
                required
                type="text"
                placeholder="Zip/Postal code"
                name="postal-code"
                autoComplete="postal-code"
                value={zipCode}
                onChange={handleZipCodeChange}
                className="step-inputBox"
              />
            </Form.Group>
          </Row>
          <div className="Company-button" style={{marginTop: "60px", marginBottom: "100px"}}>
            <Buttons 
            // handleBack={() => { navigate('../Login'); }} 
            // back={"Back"} 
            />
          </div>
        </Form>
      </div>
    </>
  );
};

export default CompanySignUp;
